/**
 * Intelligence API clients — research, hearing, review, strategy, orchestration.
 * Presentation-layer endpoints; no new AI engines.
 */

import { api } from './apiClient';
import type { WorkspaceContextPayload } from './workspaceContext';

export interface IntelligenceDiagnostics {
  generation_latency_ms?: number;
  search_latency_ms?: number;
  total_latency_ms?: number;
  reused_providers?: string[];
  orchestration_skills?: string[];
  retrieval_strategy?: string;
  authority_count?: number;
  overall_confidence?: number;
  missing_information?: string[];
  [key: string]: unknown;
}

export async function runResearchQuery(body: {
  query: string;
  workspaceId?: string;
  caseId?: string;
  caseData?: Record<string, unknown>;
  proceedings?: unknown[];
  sessionId?: string;
  topK?: number;
}): Promise<{ result: Record<string, unknown>; session: Record<string, unknown> }> {
  return api.post('/api/research/query', {
    query: body.query,
    workspaceId: body.workspaceId,
    caseId: body.caseId,
    caseData: body.caseData ?? {},
    proceedings: body.proceedings ?? [],
    sessionId: body.sessionId,
    topK: body.topK ?? 12,
  });
}

export async function prepareHearingBrief(body: {
  workspaceId: string;
  caseId: string;
  caseData?: Record<string, unknown>;
  proceedings?: unknown[];
  draftText?: string;
}): Promise<{ brief: Record<string, unknown>; diagnostics: IntelligenceDiagnostics }> {
  return api.post('/api/case/hearing/prepare', body);
}

export async function getHearingBrief(
  workspaceId: string,
  caseId: string,
): Promise<{ brief: Record<string, unknown>; diagnostics: IntelligenceDiagnostics }> {
  return api.get(`/api/case/hearing/brief/${encodeURIComponent(workspaceId)}/${encodeURIComponent(caseId)}`);
}

export async function evaluateDocumentReview(body: {
  workspaceId: string;
  caseId: string;
  caseData?: Record<string, unknown>;
  proceedings?: unknown[];
  draftText?: string;
  draftTabs?: Array<{
    id?: string;
    title?: string;
    contentExcerpt?: string;
    isActive?: boolean;
  }>;
  draftMetadata?: Record<string, unknown>;
  documents?: Record<string, unknown>[];
}): Promise<Record<string, unknown>> {
  return api.post('/api/case/document-review/evaluate', {
    workspaceId: body.workspaceId,
    caseId: body.caseId,
    caseData: body.caseData ?? {},
    proceedings: body.proceedings ?? [],
    draftText: body.draftText ?? '',
    draftTabs: body.draftTabs ?? [],
    draftMetadata: body.draftMetadata ?? {},
    documents: body.documents ?? [],
  });
}

export async function analyzeStrategy(body: {
  workspaceId: string;
  caseId: string;
  caseData?: Record<string, unknown>;
  proceedings?: unknown[];
  draftText?: string;
}): Promise<Record<string, unknown>> {
  return api.post('/api/case/strategy/analyze', body);
}

export async function fetchOrchestrationPlan(body: {
  intent?: string;
  workspaceId?: string;
  caseId?: string;
  question?: string;
  instruction?: string;
  draftText?: string;
}): Promise<Record<string, unknown>> {
  return api.post('/api/orchestration/plan', body);
}

export async function listOrchestrationSkills(): Promise<{ skills: unknown[]; count: number }> {
  return api.get('/api/orchestration/skills');
}

export async function warmCaseIntelligence(
  body: {
    workspaceId: string;
    caseId: string;
    caseData?: Record<string, unknown>;
    proceedings?: unknown[];
    draftText?: string;
  },
  signal?: AbortSignal,
): Promise<{
  warmed: boolean;
  authorities: unknown[];
  hearingBrief: Record<string, unknown>;
  diagnostics: IntelligenceDiagnostics;
}> {
  return api.post('/api/case/intelligence/warm', body, { signal });
}

export interface CaseSummaryGenerateResponse {
  summary: {
    overview: string;
    currentStatus: string;
    keyFacts: string[];
    legalIssues: string[];
    nextActions: string[];
  };
  model: string;
  generatedAt: string;
  inputHash?: string;
}

export async function generateCaseSummary(body: {
  workspaceId: string;
  caseId: string;
  caseData?: Record<string, unknown>;
  proceedings?: unknown[];
  notes?: unknown[];
  documents?: unknown[];
  inputHash?: string;
}): Promise<CaseSummaryGenerateResponse> {
  return api.post('/api/case/summary/generate', {
    workspaceId: body.workspaceId,
    caseId: body.caseId,
    caseData: body.caseData ?? {},
    proceedings: body.proceedings ?? [],
    notes: body.notes ?? [],
    documents: body.documents ?? [],
    inputHash: body.inputHash ?? '',
  });
}

export async function* generateCaseSummaryStream(
  body: {
    workspaceId: string;
    caseId: string;
    caseData?: Record<string, unknown>;
    proceedings?: unknown[];
    notes?: unknown[];
    documents?: unknown[];
    inputHash?: string;
  },
  signal?: AbortSignal,
): AsyncGenerator<{ type: string; data: unknown }> {
  for await (const event of api.stream<unknown>(
    '/api/case/summary/generate/stream',
    {
      workspaceId: body.workspaceId,
      caseId: body.caseId,
      caseData: body.caseData ?? {},
      proceedings: body.proceedings ?? [],
      notes: body.notes ?? [],
      documents: body.documents ?? [],
      inputHash: body.inputHash ?? '',
    },
    { signal },
  )) {
    yield { type: event.event, data: event.data };
  }
}

export type { WorkspaceContextPayload };
